import axios from 'axios';

// In production monolithic Docker deployment, frontend and backend share the same domain,
// so API requests must use relative paths ('/api') to reach the production server database.
// In local development (localhost), requests route to the local dev backend ('http://localhost:8001').
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    }
    // In production (e.g. sleonline.in), same-origin relative path reaches the live server API
    return '';
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API_URL = getBackendUrl();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sle_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('sle_admin_token');
      localStorage.removeItem('sle_admin');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin') {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

// Public APIs
export const getCategories = () => api.get('/categories');
export const getCategory = (id) => api.get(`/categories/${id}`);
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const getTestimonials = () => api.get('/testimonials');
export const createInquiry = (data) => api.post('/inquiries', data);
export const createContactMessage = (data) => api.post('/contact', data);
export const downloadCatalogue = () => `${API_URL}/api/catalogue/download`;
export const seedData = () => api.post('/admin/seed');

// Smart Search & TypeSafe AI APIs
export const smartSearch = (data) => api.post('/search/smart', data);
export const getSearchSuggestions = () => api.get('/search/suggestions');
export const getProductRecommendations = (productId) => api.get(`/products/${productId}/recommendations`);

// Analytics & Telemetry APIs
export const trackAnalyticsEvent = (data) => api.post('/analytics/event', data);
export const getAnalyticsOverview = (params) => api.get('/admin/analytics/overview', { params });

// Auth APIs
export const loginAdmin = (data) => api.post('/auth/login', data);
export const registerAdmin = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// Admin APIs
export const createCategory = (data) => api.post('/admin/categories', data);
export const updateCategory = (id, data) => api.put(`/admin/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/admin/categories/${id}`);
export const reorderCategories = (order) => api.put('/admin/categories/reorder', { order });

export const createProduct = (data) => api.post('/admin/products', data);
export const updateProduct = (id, data) => api.put(`/admin/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/admin/products/${id}`);
export const reorderProducts = (productIds) => api.put('/admin/products/reorder/batch', { product_ids: productIds });

export const getInquiries = () => api.get('/admin/inquiries');
export const updateInquiryStatus = (id, status) => api.put(`/admin/inquiries/${id}/status?status=${status}`);

export const getContactMessages = () => api.get('/admin/contacts');
export const updateContactStatus = (id, status) => api.put(`/admin/contacts/${id}/status?status=${status}`);

export const createTestimonial = (data) => api.post('/admin/testimonials', data);
export const updateTestimonial = (id, data) => api.put(`/admin/testimonials/${id}`, data);
export const deleteTestimonial = (id) => api.delete(`/admin/testimonials/${id}`);

export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getSiteSettings = () => api.get('/settings');
export const updateSiteSettings = (data) => api.put('/admin/settings', data);

export const uploadCatalogue = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/catalogue/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteCatalogue = () => api.delete('/admin/catalogue');

export const getQuotations = () => api.get('/admin/quotations');
export const createQuotation = (data) => api.post('/admin/quotations', data);
export const deleteQuotation = (id) => api.delete(`/admin/quotations/${id}`);
export const getQuotationPdfUrl = (id) => `${API_URL}/api/admin/quotations/${id}/pdf`;
export const sendQuotationEmail = (id, data) => api.post(`/admin/quotations/${id}/send-email`, data);

// Clients
export const getClients = () => api.get('/clients');
export const createClient = (data) => api.post('/admin/clients', data);
export const updateClient = (id, data) => api.put(`/admin/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/admin/clients/${id}`);
export const reorderClients = (order) => api.put('/admin/clients/reorder', { order });

// User Management
export const getUsers = () => api.get('/admin/users');
export const createUser = (data) => api.post('/admin/users', data);
export const updateUser = (id, data) => api.put(`/admin/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/admin/users/${id}`);

// Hero Slides
export const getHeroSlides = () => api.get('/hero-slides');
export const getAdminHeroSlides = () => api.get('/admin/hero-slides');
export const createHeroSlide = (data) => api.post('/admin/hero-slides', data);
export const updateHeroSlide = (id, data) => api.put(`/admin/hero-slides/${id}`, data);
export const deleteHeroSlide = (id) => api.delete(`/admin/hero-slides/${id}`);
export const reorderHeroSlides = (order) => api.put('/admin/hero-slides/reorder', { order });

// Sustainability
export const getSustainabilityContent = () => api.get('/sustainability');
export const getSustainabilitySettings = () => api.get('/admin/sustainability/settings');
export const updateSustainabilitySettings = (data) => api.put('/admin/sustainability/settings', data);
export const getAdminEcoShowcases = () => api.get('/admin/sustainability/eco-showcases');
export const createEcoShowcase = (data) => api.post('/admin/sustainability/eco-showcases', data);
export const updateEcoShowcase = (id, data) => api.put(`/admin/sustainability/eco-showcases/${id}`, data);
export const deleteEcoShowcase = (id) => api.delete(`/admin/sustainability/eco-showcases/${id}`);
export const reorderEcoShowcases = (order) => api.put('/admin/sustainability/eco-showcases/reorder', { order });

// Audit Logs
export const getAuditLogs = (params) => api.get('/admin/audit-logs', { params });
export const getAuditStats = () => api.get('/admin/audit-logs/stats');
export const getAuditLog = (id) => api.get(`/admin/audit-logs/${id}`);

export default api;
